import cn from 'classnames';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { getIconForFormat } from '@bibliocommons/utils-format';
import { metadataIdFixer } from '@bibliocommons/utils-link';
import { bindAll } from '@bibliocommons/utils-react';
import * as Icons from '@bibliocommons/deprecated-base-icons';
import manageFocus from '@bibliocommons/base-accessibility-manage-focus';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { Generic } from '@bibliocommons/shared-messages';
import { TextButton } from '@bibliocommons/deprecated-base-buttons';
import AvailabilityBibBlock from '@bibliocommons/availability-bib-block';
import loadable from '@loadable/component';
import AddToShelfComboButtonContainer from 'app/components/deprecated/shared/AddToShelfComboButton/AddToShelfComboButtonContainer';
import MessagingContainer from 'app/components/Messaging/MessagingContainer';
import FormatInfoContainer from 'app/components/shared/FormatInfo/FormatInfoContainer';

import { ShelfMessage } from '../../shared/Messages';

import GroupingSummary from '../GroupingSummary';
import ItemTransactionButtonContainer from '../../shared/ItemTransactionButton/ItemTransactionButtonContainer';
import GroupSearchFeedbackButton from '../../GroupSearchFeedbackButton';
import './ManifestationListItem.scss';

const WorkflowContainer = loadable(() => import('app/components/Workflow/WorkflowContainer'));

class ManifestationListItem extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
    this.state = {
      transactionButtonToggleOpen: this.props.drawerOpen || false
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.transactionButtonToggleOpen && !prevState.transactionButtonToggleOpen) {
      this.props.captureFocus(this._actionDrawer, false);
    } else {
      this.props.restoreFocus();
    }
  }

  handleToggleClick(e) {
    e.preventDefault();

    return this.setState(state => ({
      transactionButtonToggleOpen: !state.transactionButtonToggleOpen
    }));
  }

  renderGroupSummary(item) {
    const { catalogSearchOptions, groupSearchFeedbackActions, showGroupingDebug } = this.props;
    if (catalogSearchOptions.get('showGroupings') && showGroupingDebug) {
      return (
        <div className="manifestation-item-debug-actions hidden-xs">
          <GroupingSummary groupSummary={item.get('groupSearchSummary')} />
          <GroupSearchFeedbackButton handleClick={groupSearchFeedbackActions.startGroupSearchFeedback} bib={item} />
        </div>
      );
    }
    return null;
  }

  renderManifestationItemToggle() {
    const { transactionButtonToggleOpen } = this.state;
    const { editable } = this.props;
    const itemId = this.props.item.get('id');
    const toggleIcon = editable ? (
      <Icons.PencilEdit width={20} height={20} color="#02729e" />
    ) : (
      <Icons.More width={20} height={20} color="#02729e" />
    );

    return (
      <div
        className={cn('manifestation-item-action-toggle', 'hidden-sm hidden-md hidden-lg', {
          'visible-xs toggled-open': transactionButtonToggleOpen
        })}
      >
        <TextButton
          handleClick={this.handleToggleClick}
          id={`manifestation-item-action-wrap-${itemId}`}
          aria-expanded={transactionButtonToggleOpen}
        >
          {toggleIcon}
          <ScreenReaderMessage>
            <FormattedMessage id="ada_toggle_manifestation_item_actions" />
          </ScreenReaderMessage>
        </TextButton>
      </div>
    );
  }

  render() {
    const { item, showHoldCount, reciprocalRank } = this.props;
    const { transactionButtonToggleOpen } = this.state;
    const itemId = item.get('id');
    const bibTitle = item.getIn(['briefInfo', 'title']);
    const statusType = item.getIn(['availability', 'statusType']);

    return (
      <WorkflowContainer id={itemId} reciprocalRank={reciprocalRank}>
        {workflowComponent =>
          workflowComponent || (
            <div className="manifestation-item cp-manifestation-list-item row" key={itemId}>
              <MessagingContainer id={itemId}>
                {(messageOptions, dispatch) =>
                  messageOptions.message.get('type') === 'SHELF' ? (
                    <ShelfMessage
                      {...messageOptions}
                      dispatch={dispatch}
                      values={messageOptions.message.get('values', Immutable.Map()).merge({
                        title: bibTitle,
                        username: this.props.currentUser.get('name')
                      })}
                    />
                  ) : (
                    <Generic
                      {...messageOptions}
                      values={messageOptions.message.get('values', Immutable.Map()).merge({ title: bibTitle })}
                    />
                  )}
              </MessagingContainer>
              <div className={`manifestation-item-format-call-wrap ${statusType.toLowerCase()}`}>
                <div className="manifestation-item-format-info-wrap">
                  <span className={getIconForFormat(item.getIn(['briefInfo', 'format']))} aria-hidden />
                  <a
                    href={`/item/show/${metadataIdFixer(itemId)}`}
                    target="_parent"
                    lang={item.get('primaryLanguage')}
                    rel="noopener"
                    data-test-id={`item-link-${itemId}`}
                    data-key="bib-manifestation-item-link"
                  >
                    <FormatInfoContainer
                      item={item}
                      className="manifestation-item-format-info"
                      searchScopeIsCurrent={this.props.searchScopeIsCurrent}
                    />
                  </a>
                </div>
                <div className="manifestation-item-availability-block-wrap">
                  <AvailabilityBibBlock
                    bib={item}
                    availabilityStatus={item.get('availability')}
                    availabilityDetail={this.props.availabilityDetail}
                    availabilityDetailActions={this.props.availabilityDetailActions}
                    showHoldCount={showHoldCount}
                    reciprocalRank={reciprocalRank}
                  />
                </div>
              </div>
              {this.renderManifestationItemToggle()}
              <div
                className={`manifestation-item-action-wrap ${transactionButtonToggleOpen ? 'toggled-open' : ''}`}
                ref={el => {
                  this._actionDrawer = el;
                }}
              >
                <ItemTransactionButtonContainer id={itemId} messagingId={itemId} />
                <div className="add-to-shelf-wrap">
                  <AddToShelfComboButtonContainer metadataId={itemId} reciprocalRank={reciprocalRank} />
                </div>
                {this.renderGroupSummary(item)}
              </div>
            </div>
          )}
      </WorkflowContainer>
    );
  }
}

ManifestationListItem.propTypes = {
  availabilityDetail: ImmutablePropTypes.map,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func),
  catalogSearchOptions: ImmutablePropTypes.map,
  currentUser: ImmutablePropTypes.map,
  item: ImmutablePropTypes.map,
  drawerOpen: PropTypes.bool,
  showHoldCount: PropTypes.bool,
  reciprocalRank: PropTypes.number,
  groupSearchFeedbackActions: PropTypes.objectOf(PropTypes.func),
  showGroupingDebug: PropTypes.bool,
  editable: PropTypes.bool,
  searchScopeIsCurrent: PropTypes.bool,
  restoreFocus: PropTypes.func,
  captureFocus: PropTypes.func
};

export default manageFocus(ManifestationListItem);
